import { ref } from 'vue';
import { defineStore } from 'pinia';
import { createStorage } from '@aviata/storage';
import { cloneDeep } from 'lodash-es';
import { flightEntity } from 'freedom-shared';
import type { Airflow, Dict } from '@aviata/platform-types';

import { mapSavedPassenger, getDefaultState } from './lib';
import type {
  BookingState,
  MedicalInsuranceProductsState,
  SavePassengerPayload,
  NormalizedPassenger,
  FlightDelayBooking,
} from './types';

const bookingDataKey = 'freedom-avia:booking-data';
const bookingDataStorage = createStorage(sessionStorage);

export const useBookingStore = defineStore('booking', () => {
  const offer = ref<Airflow.Offer.Details['offer'] | null>(null);
  const passengersSchema = ref<Airflow.Offer.Schema.Details | null>(null);
  const dict = ref<Dict.Entity | null>();

  const passengersSaveResponse = ref<NormalizedPassenger[] | null>(null);
  const passengersSavePayload = ref<SavePassengerPayload | null>(null);

  const rawData = bookingDataStorage.parse(bookingDataKey);
  const defaultState = getDefaultState(rawData);
  const state = ref(defaultState);

  const mappedFlights = ref<flightEntity.types.MappedFlight[]>([]);
  const medicalInsuranceProducts = ref<MedicalInsuranceProductsState[]>([]);
  const flightDelayProducts = ref<FlightDelayBooking[] | null>(null);

  /**
   * Per-offer fare-family selection persisted across QuickBookingPage
   * remounts, so navigation away to seat-map / baggage and back keeps the
   * user's tariff choice. Map is keyed by `groupKey` and stores the chosen
   * fare's id; reset on offer change.
   */
  const selectedFareIds = ref<Record<string, string> | null>(null);
  /** Offer id the cached `selectedFareIds` belongs to. */
  const selectedFareIdsOfferId = ref<string | null>(null);

  const update = (updatedState: Partial<BookingState>) => {
    state.value = { ...state.value, ...updatedState };
    bookingDataStorage.stringify(bookingDataKey, state.value);
  };

  function init(details: Airflow.Offer.Details) {
    update(defaultState);
    offer.value = details.offer;
    passengersSchema.value = details.schema;
    dict.value = details.dict;

    onUpdateFlights(details);
  }

  function updateMedicalInsuranceProducts(
    products: MedicalInsuranceProductsState[],
  ) {
    medicalInsuranceProducts.value = [...products];
  }

  function updateFlightDelayProducts(bookings: FlightDelayBooking[]) {
    flightDelayProducts.value = [...bookings];
  }

  function nullifyFlightDelayProducts() {
    flightDelayProducts.value = null;
  }

  function setPassengersSaveResponse(response: Airflow.Booking.Passenger[]) {
    passengersSaveResponse.value = response.map(mapSavedPassenger);
  }

  function setPassengersSavePayload(payload: SavePassengerPayload) {
    passengersSavePayload.value = cloneDeep(payload);
  }

  function onUpdateFlights(details: Airflow.Offer.Details) {
    mappedFlights.value = details.offer.flights.map(flight =>
      flightEntity.lib.mapFlight(details.dict, flight),
    );
  }

  function setSelectedFareIds(
    offerId: string,
    ids: Record<string, string> | null,
  ) {
    selectedFareIdsOfferId.value = offerId;
    selectedFareIds.value = ids;
  }

  function resetSelectedFareIds() {
    selectedFareIds.value = null;
    selectedFareIdsOfferId.value = null;
  }

  return {
    state,
    dict,
    passengersSchema,
    offer,
    mappedFlights,
    init,
    update,
    updateMedicalInsuranceProducts,
    medicalInsuranceProducts,
    setPassengersSavePayload,
    setPassengersSaveResponse,
    passengersSaveResponse,
    passengersSavePayload,
    onUpdateFlights,
    updateFlightDelayProducts,
    flightDelayProducts,
    nullifyFlightDelayProducts,
    selectedFareIds,
    selectedFareIdsOfferId,
    setSelectedFareIds,
    resetSelectedFareIds,
  };
});
